'use strict';

const BRAND = "XdrError";
class XdrError extends Error {
  constructor(message) {
    super(message);
    this.name = BRAND;
  }
  /**
   * Brand-based `instanceof` so the check keeps working when the ESM and CJS
   * builds of this package coexist in one dependency graph (each build has its
   * own class identity, so a reference-equality `instanceof` would fail).
   */
  static [Symbol.hasInstance](value) {
    return value instanceof Error && value.name === BRAND;
  }
}

function paddingLength(length) {
  return (4 - length % 4) % 4;
}
function isPlainObject(value) {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
function assertLength(value, name) {
  if (!Number.isInteger(value) || value < 0 || value > 4294967295) {
    throw new XdrError(`${name}: expected uint32 length`);
  }
}
function assertIntRange(value, min, max, path) {
  if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
    throw new XdrError(`${path}: expected integer in range ${min}..${max}`);
  }
}
function assertBigIntRange(value, min, max, path) {
  if (typeof value !== "bigint" || value < min || value > max) {
    throw new XdrError(`${path}: expected bigint in range ${min}..${max}`);
  }
}
function assertNumber(value, path) {
  if (typeof value !== "number") {
    throw new XdrError(`${path}: expected number`);
  }
}
function assertUint8Array(value, path) {
  if (!(value instanceof Uint8Array)) {
    throw new XdrError(`${path}: expected Uint8Array`);
  }
  try {
    Uint8Array.prototype.at.call(value, 0);
  } catch {
    throw new XdrError(`${path}: Uint8Array is detached or out of bounds`);
  }
}
function assertArray(value, path) {
  if (!Array.isArray(value)) {
    throw new XdrError(`${path}: expected array`);
  }
}

const DEFAULT_MAX_DEPTH = 1500;
class Reader {
  #offset = 0;
  #depth = 0;
  #maxDepth;
  /**
   * One view over the whole input, reused by every scalar read. Building a
   * fresh `DataView` per read (over a fresh `slice()` of the bytes) costs two
   * allocations per integer, which dominates decoding for union- and
   * struct-heavy schemas like Stellar's.
   */
  #view;
  /**
   * Input length, snapshot from the view. Bounds checks must use this rather
   * than the live `bytes.length`: over a resizable ArrayBuffer the two can
   * disagree after a resize, and a check that passes against bytes the view
   * cannot see turns into a raw RangeError from the scalar read.
   */
  #length;
  bytes;
  constructor(bytes, maxDepth = DEFAULT_MAX_DEPTH) {
    if (maxDepth < 0 || !Number.isInteger(maxDepth)) {
      throw new XdrError(`invalid maxDepth ${maxDepth}`);
    }
    this.#maxDepth = maxDepth;
    if (!ArrayBuffer.isView(bytes) || bytes.BYTES_PER_ELEMENT !== 1) {
      throw new TypeError("Reader expects a Uint8Array");
    }
    this.bytes = Object.getPrototypeOf(bytes) === Uint8Array.prototype ? bytes : bytes.byteLength === 0 ? new Uint8Array(0) : new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    this.#view = this.bytes.buffer?.detached ? new DataView(new ArrayBuffer(0)) : new DataView(
      this.bytes.buffer,
      this.bytes.byteOffset,
      this.bytes.byteLength
    );
    this.#length = this.#view.byteLength;
  }
  /**
   * Reserve `size` bytes for an in-place scalar read and return the offset to
   * read them from.
   */
  #takeScalar(size, path) {
    if (this.remaining < size) {
      throw new XdrError(
        `${path}: incomplete XDR data at offset ${this.#offset}, expected ${size} byte(s)`
      );
    }
    const at = this.#offset;
    this.#offset += size;
    return at;
  }
  get offset() {
    return this.#offset;
  }
  get remaining() {
    return this.#length - this.#offset;
  }
  enter(path) {
    this.#depth += 1;
    if (this.#depth > this.#maxDepth) {
      this.#depth -= 1;
      throw new XdrError(
        `${path}: max recursion depth ${this.#maxDepth} exceeded`
      );
    }
  }
  exit() {
    this.#depth -= 1;
  }
  done(path) {
    if (this.remaining !== 0) {
      throw new XdrError(
        `${path}: trailing ${this.remaining} byte(s) after XDR value`
      );
    }
  }
  readBytes(length, path) {
    if (length < 0 || !Number.isInteger(length)) {
      throw new XdrError(`${path}: invalid byte length ${length}`);
    }
    if (this.remaining < length) {
      throw new XdrError(
        `${path}: incomplete XDR data at offset ${this.#offset}, expected ${length} byte(s)`
      );
    }
    const result = this.bytes.slice(this.#offset, this.#offset + length);
    if (result.length !== length) {
      throw new XdrError(`${path}: input shrank during decode`);
    }
    this.#offset += length;
    return result;
  }
  skipPadding(length, path) {
    if (length < 0 || !Number.isInteger(length)) {
      throw new XdrError(`${path}: invalid byte length ${length}`);
    }
    const padding = paddingLength(length);
    if (padding === 0) {
      return;
    }
    const at = this.#takeScalar(padding, path);
    for (let i = 0; i < padding; i++) {
      if (this.bytes[at + i] !== 0) {
        this.#offset = at;
        throw new XdrError(`${path}: non-zero XDR padding`);
      }
    }
  }
  readInt32(path) {
    return this.#view.getInt32(this.#takeScalar(4, path), false);
  }
  readUint32(path) {
    return this.#view.getUint32(this.#takeScalar(4, path), false);
  }
  readBigInt64(path) {
    return this.#view.getBigInt64(this.#takeScalar(8, path), false);
  }
  readBigUint64(path) {
    return this.#view.getBigUint64(this.#takeScalar(8, path), false);
  }
  readFloat32(path) {
    return this.#view.getFloat32(this.#takeScalar(4, path), false);
  }
  readFloat64(path) {
    return this.#view.getFloat64(this.#takeScalar(8, path), false);
  }
}

const INITIAL_BUFFER_SIZE = 8192;
class Writer {
  #buffer = new Uint8Array(INITIAL_BUFFER_SIZE);
  /**
   * View over `#buffer`, reused by every scalar write and rebuilt only when
   * the buffer grows. Writing through a per-call `Uint8Array` + `DataView`
   * pair costs two allocations and a copy per integer.
   */
  #view = new DataView(this.#buffer.buffer);
  #offset = 0;
  #depth = 0;
  #maxDepth;
  constructor(maxDepth = DEFAULT_MAX_DEPTH) {
    if (maxDepth < 0 || !Number.isInteger(maxDepth)) {
      throw new XdrError(`invalid maxDepth ${maxDepth}`);
    }
    this.#maxDepth = maxDepth;
  }
  get offset() {
    return this.#offset;
  }
  enter(path) {
    this.#depth += 1;
    if (this.#depth > this.#maxDepth) {
      this.#depth -= 1;
      throw new XdrError(
        `${path}: max recursion depth ${this.#maxDepth} exceeded`
      );
    }
  }
  exit() {
    this.#depth -= 1;
  }
  writeBytes(bytes) {
    this.#ensureCapacity(bytes.length);
    this.#buffer.set(bytes, this.#offset);
    this.#offset += bytes.length;
  }
  writePadding(length) {
    const padding = paddingLength(length);
    if (padding > 0) {
      this.#ensureCapacity(padding);
      this.#buffer.fill(0, this.#offset, this.#offset + padding);
      this.#offset += padding;
    }
  }
  writeInt32(value) {
    const offset = this.#prepareScalar(4);
    this.#view.setInt32(offset, value, false);
    this.#offset += 4;
  }
  writeUint32(value) {
    const offset = this.#prepareScalar(4);
    this.#view.setUint32(offset, value, false);
    this.#offset += 4;
  }
  writeBigInt64(value) {
    const offset = this.#prepareScalar(8);
    this.#view.setBigInt64(offset, value, false);
    this.#offset += 8;
  }
  writeBigUint64(value) {
    const offset = this.#prepareScalar(8);
    this.#view.setBigUint64(offset, value, false);
    this.#offset += 8;
  }
  writeFloat32(value) {
    const offset = this.#prepareScalar(4);
    this.#view.setFloat32(offset, value, false);
    this.#offset += 4;
  }
  writeFloat64(value) {
    const offset = this.#prepareScalar(8);
    this.#view.setFloat64(offset, value, false);
    this.#offset += 8;
  }
  /**
   * Ensure a scalar fits and return its starting offset without advancing.
   * Callers commit the offset only after the DataView write succeeds.
   */
  #prepareScalar(size) {
    this.#ensureCapacity(size);
    return this.#offset;
  }
  toUint8Array() {
    return this.#buffer.slice(0, this.#offset);
  }
  #ensureCapacity(additionalBytes) {
    const requiredLength = this.#offset + additionalBytes;
    if (requiredLength <= this.#buffer.length) {
      return;
    }
    let nextLength = this.#buffer.length;
    while (nextLength < requiredLength) {
      nextLength *= 2;
    }
    const nextBuffer = new Uint8Array(nextLength);
    nextBuffer.set(this.#buffer);
    this.#buffer = nextBuffer;
    this.#view = new DataView(nextBuffer.buffer);
  }
}

const UNBOUNDED_MAX_LENGTH = 4294967295;
class BaseType {
  name;
  constructor(name = void 0) {
    this.name = name;
  }
  encode(value, options) {
    const writer = new Writer(options?.maxDepth);
    this._write(value, writer, this.name ?? this.kind);
    return writer.toUint8Array();
  }
  decode(input, options) {
    const reader = new Reader(input, options?.maxDepth);
    const value = this._read(reader, this.name ?? this.kind);
    reader.done(this.name ?? this.kind);
    return value;
  }
  validateXdr(input, options) {
    try {
      this.decode(input, options);
      return true;
    } catch (error) {
      if (error instanceof XdrError) {
        return false;
      }
      throw error;
    }
  }
  validate(value) {
    try {
      const writer = new Writer();
      this._write(value, writer, this.name ?? this.kind);
      return true;
    } catch (error) {
      if (error instanceof XdrError) {
        return false;
      }
      throw error;
    }
  }
}

class ArrayType extends BaseType {
  kind = "array";
  element;
  maxLength;
  constructor(element, maxLength) {
    super();
    assertLength(maxLength, "array maxLength");
    this.element = element;
    this.maxLength = maxLength;
  }
  _read(reader, path) {
    reader.enter(path);
    try {
      const length = reader.readUint32(path);
      if (length > this.maxLength) {
        throw new XdrError(
          `${path}: array length ${length} exceeds maximum ${this.maxLength}`
        );
      }
      if (length > reader.remaining) {
        throw new XdrError(
          `${path}: array length ${length} exceeds remaining ${reader.remaining} byte(s)`
        );
      }
      return readArray(reader, length, this.element, path);
    } finally {
      reader.exit();
    }
  }
  _write(value, writer, path) {
    writer.enter(path);
    try {
      assertArray(value, path);
      if (value.length > this.maxLength) {
        throw new XdrError(
          `${path}: array length ${value.length} exceeds maximum ${this.maxLength}`
        );
      }
      writer.writeUint32(value.length);
      writeArray(value, writer, this.element, path);
    } finally {
      writer.exit();
    }
  }
}
function array(element, maxLength) {
  return new ArrayType(element, maxLength);
}
function readArray(reader, length, element, path) {
  const values = [];
  for (let i = 0; i < length; i += 1) {
    const before = reader.offset;
    const value = element._read(reader, `${path}[${i}]`);
    if (reader.offset === before) {
      throw new XdrError(
        `${path}[${i}]: element type '${element.name ?? element.kind}' consumed no input; an array element must encode to at least one byte`
      );
    }
    values.push(value);
  }
  return values;
}
function writeArray(values, writer, element, path) {
  let i = 0;
  for (const value of values) {
    const before = writer.offset;
    element._write(value, writer, `${path}[${i}]`);
    if (writer.offset === before) {
      throw new XdrError(
        `${path}[${i}]: element type '${element.name ?? element.kind}' encoded to zero bytes; an array element must encode to at least one byte`
      );
    }
    i += 1;
  }
}

class BoolType extends BaseType {
  kind = "bool";
  _read(reader, path) {
    const value = reader.readUint32(path);
    if (value === 0) return false;
    if (value === 1) return true;
    throw new XdrError(`${path}: invalid bool discriminant ${value}`);
  }
  _write(value, writer, path) {
    if (typeof value !== "boolean") {
      throw new XdrError(`${path}: expected boolean`);
    }
    writer.writeUint32(value ? 1 : 0);
  }
}
const BOOL_TYPE = new BoolType();
function bool() {
  return BOOL_TYPE;
}

class DoubleType extends BaseType {
  kind = "double";
  _read(reader, path) {
    return reader.readFloat64(path);
  }
  _write(value, writer, path) {
    assertNumber(value, path);
    writer.writeFloat64(value);
  }
}
function double() {
  return new DoubleType();
}

const RESERVED_ENUM_MEMBER_NAMES = /* @__PURE__ */ new Set([
  // Assigning '__proto__' onto the schema would invoke the inherited prototype
  // setter instead of defining a member constant.
  "__proto__",
  "name",
  "kind",
  "encode",
  "decode",
  "validate",
  "_read",
  "_write",
  "nameByValue"
]);
class EnumType extends BaseType {
  kind = "enum";
  // Maps wire value → declared member name. Public so the generic toJson
  // walker can render numeric enums as their string names.
  nameByValue;
  #valuesSet = /* @__PURE__ */ new Set();
  constructor(name, values) {
    super(name);
    const nameByValue = /* @__PURE__ */ new Map();
    for (const [memberName, memberValue] of Object.entries(values)) {
      assertIntRange(
        memberValue,
        -2147483648,
        2147483647,
        `${name}.${memberName}`
      );
      if (this.#valuesSet.has(memberValue)) {
        throw new XdrError(`${name}: duplicate enum value ${memberValue}`);
      }
      this.#valuesSet.add(memberValue);
      nameByValue.set(memberValue, memberName);
    }
    this.nameByValue = nameByValue;
  }
  _read(reader, path) {
    const value = reader.readInt32(path);
    if (!this.#valuesSet.has(value)) {
      throw new XdrError(`${path}: unknown enum value ${value}`);
    }
    return value;
  }
  _write(value, writer, path) {
    if (typeof value !== "number" || !this.#valuesSet.has(value)) {
      throw new XdrError(`${path}: unknown enum value ${String(value)}`);
    }
    writer.writeInt32(value);
  }
}
function enumType(name, values) {
  for (const memberName of Object.getOwnPropertyNames(values)) {
    if (RESERVED_ENUM_MEMBER_NAMES.has(memberName)) {
      throw new XdrError(
        `${name}: enum member name ${memberName} collides with reserved property`
      );
    }
  }
  const schema = new EnumType(name, values);
  return Object.assign(schema, values);
}

class FixedArrayType extends BaseType {
  kind = "fixedArray";
  element;
  length;
  constructor(element, length) {
    super();
    assertLength(length, "fixedArray length");
    this.element = element;
    this.length = length;
  }
  _read(reader, path) {
    reader.enter(path);
    try {
      if (this.length > reader.remaining) {
        throw new XdrError(
          `${path}: array length ${this.length} exceeds remaining ${reader.remaining} byte(s)`
        );
      }
      return readArray(reader, this.length, this.element, path);
    } finally {
      reader.exit();
    }
  }
  _write(value, writer, path) {
    writer.enter(path);
    try {
      assertArray(value, path);
      if (value.length !== this.length) {
        throw new XdrError(
          `${path}: expected array length ${this.length}, got ${value.length}`
        );
      }
      writeArray(value, writer, this.element, path);
    } finally {
      writer.exit();
    }
  }
}
function fixedArray(element, length) {
  return new FixedArrayType(element, length);
}

class FloatType extends BaseType {
  kind = "float";
  _read(reader, path) {
    return reader.readFloat32(path);
  }
  _write(value, writer, path) {
    assertNumber(value, path);
    writer.writeFloat32(value);
  }
}
function float() {
  return new FloatType();
}

class IntType extends BaseType {
  kind = "int32";
  _read(reader, path) {
    return reader.readInt32(path);
  }
  _write(value, writer, path) {
    assertIntRange(value, -2147483648, 2147483647, path);
    writer.writeInt32(value);
  }
}
function int32() {
  return new IntType();
}

class HyperType extends BaseType {
  kind = "int64";
  _read(reader, path) {
    return reader.readBigInt64(path);
  }
  _write(value, writer, path) {
    assertBigIntRange(value, -(1n << 63n), (1n << 63n) - 1n, path);
    writer.writeBigInt64(value);
  }
}
function int64() {
  return new HyperType();
}

class LazyType extends BaseType {
  kind = "lazy";
  getSchema;
  constructor(getSchema) {
    super();
    this.getSchema = getSchema;
  }
  _read(reader, path) {
    reader.enter(path);
    try {
      return this.getSchema()._read(reader, path);
    } finally {
      reader.exit();
    }
  }
  _write(value, writer, path) {
    writer.enter(path);
    try {
      this.getSchema()._write(value, writer, path);
    } finally {
      writer.exit();
    }
  }
}
function lazy(getSchema) {
  return new LazyType(() => getSchema());
}

class OpaqueType extends BaseType {
  constructor(length, name) {
    super(name);
    this.length = length;
    assertLength(length, "opaque length");
  }
  length;
  kind = "opaque";
  _read(reader, path) {
    const bytes = reader.readBytes(this.length, path);
    reader.skipPadding(this.length, path);
    return bytes;
  }
  _write(value, writer, path) {
    assertUint8Array(value, path);
    if (value.length !== this.length) {
      throw new XdrError(
        `${path}: expected ${this.length} byte(s), got ${value.length}`
      );
    }
    writer.writeBytes(value);
    writer.writePadding(value.length);
  }
}
function opaque(length, name) {
  return new OpaqueType(length, name);
}

class OptionType extends BaseType {
  kind = "option";
  element;
  constructor(element) {
    super();
    this.element = element;
  }
  _read(reader, path) {
    reader.enter(path);
    try {
      const present = BOOL_TYPE._read(reader, `${path}.present`);
      if (!present) {
        return null;
      }
      const value = this.element._read(reader, `${path}.value`);
      if (value === null) {
        throw new XdrError(
          `${path}: present option decoded to null, which is how an absent option is represented; wrap the element in a single-field struct to distinguish the two`
        );
      }
      return value;
    } finally {
      reader.exit();
    }
  }
  _write(value, writer, path) {
    writer.enter(path);
    try {
      BOOL_TYPE._write(value !== null, writer, `${path}.present`);
      if (value !== null) {
        this.element._write(value, writer, `${path}.value`);
      }
    } finally {
      writer.exit();
    }
  }
}
function option(element) {
  if (element.kind === "option") {
    throw new XdrError(
      "option: cannot nest option directly inside option, since an absent outer option and a present-but-absent inner one are both `null`; wrap the inner option in a single-field struct instead"
    );
  }
  return new OptionType(element);
}

class StringType extends BaseType {
  constructor(maxLength) {
    super();
    this.maxLength = maxLength;
    assertLength(maxLength, "string maxLength");
  }
  maxLength;
  kind = "string";
  _read(reader, path) {
    const length = reader.readUint32(path);
    if (length > this.maxLength) {
      throw new XdrError(
        `${path}: string byte length ${length} exceeds maximum ${this.maxLength}`
      );
    }
    const bytes = reader.readBytes(length, path);
    reader.skipPadding(length, path);
    return bytes;
  }
  _write(value, writer, path) {
    assertUint8Array(value, path);
    if (value.length > this.maxLength) {
      throw new XdrError(
        `${path}: string byte length ${value.length} exceeds maximum ${this.maxLength}`
      );
    }
    writer.writeUint32(value.length);
    writer.writeBytes(value);
    writer.writePadding(value.length);
  }
}
function string_(maxLength) {
  return new StringType(maxLength);
}

class StructType extends BaseType {
  kind = "struct";
  // Ordered [fieldName, schema] pairs. Public so the generic toJson/fromJson
  // walker can introspect struct shape without going through internals.
  entries;
  constructor(name, fields) {
    super(name);
    if (Object.getOwnPropertyNames(fields).includes("__proto__")) {
      throw new XdrError(
        `${name}: struct field name '__proto__' is not allowed`
      );
    }
    this.entries = Object.entries(fields);
  }
  _read(reader, path) {
    reader.enter(path);
    try {
      const value = {};
      for (const [key, schema] of this.entries) {
        value[key] = schema._read(reader, `${path}.${key}`);
      }
      return value;
    } finally {
      reader.exit();
    }
  }
  _write(value, writer, path) {
    writer.enter(path);
    try {
      if (!isPlainObject(value)) {
        throw new XdrError(`${path}: expected plain object`);
      }
      const record = value;
      for (const [key, schema] of this.entries) {
        if (!Object.prototype.hasOwnProperty.call(record, key)) {
          throw new XdrError(`${path}.${key}: missing struct field`);
        }
        schema._write(record[key], writer, `${path}.${key}`);
      }
    } finally {
      writer.exit();
    }
  }
}
function struct(name, fields) {
  return new StructType(name, fields);
}

class UIntType extends BaseType {
  kind = "uint32";
  _read(reader, path) {
    return reader.readUint32(path);
  }
  _write(value, writer, path) {
    assertIntRange(value, 0, 4294967295, path);
    writer.writeUint32(value);
  }
}
function uint32() {
  return new UIntType();
}

class UHyperType extends BaseType {
  kind = "uint64";
  _read(reader, path) {
    return reader.readBigUint64(path);
  }
  _write(value, writer, path) {
    assertBigIntRange(value, 0n, (1n << 64n) - 1n, path);
    writer.writeBigUint64(value);
  }
}
function uint64() {
  return new UHyperType();
}

const DEFAULT_CASE_NAME = "default";
class UnionType extends BaseType {
  kind = "union";
  // Exposed publicly so the generic toJson/fromJson walker can introspect
  // union shape (discriminator schema, case list, default arm, switch field).
  switchOn;
  cases;
  defaultArm;
  switchKey;
  #casesByDiscriminant = /* @__PURE__ */ new Map();
  constructor(name, switchOn, cases, defaultArm, switchKey) {
    super(name);
    if (switchKey === "__proto__") {
      throw new XdrError(`${name}: switchKey '__proto__' is not allowed`);
    }
    this.switchOn = switchOn;
    this.cases = cases;
    this.defaultArm = defaultArm;
    this.switchKey = switchKey;
    if (cases.length === 0 && defaultArm === void 0) {
      throw new XdrError(
        `${name}: union needs at least one case or a default arm`
      );
    }
    const names = /* @__PURE__ */ new Set();
    for (const unionCase of cases) {
      if (!switchOn.validate(unionCase.discriminant)) {
        throw new XdrError(
          `${name}.${unionCase.name}: case discriminator ${String(
            unionCase.discriminant
          )} is not a valid ${switchOn.name ?? switchOn.kind} value`
        );
      }
      if (names.has(unionCase.name)) {
        throw new XdrError(
          `${name}: duplicate union case name ${unionCase.name}`
        );
      }
      names.add(unionCase.name);
      if (this.#casesByDiscriminant.has(unionCase.discriminant)) {
        throw new XdrError(
          `${name}: duplicate union discriminator ${String(
            unionCase.discriminant
          )}`
        );
      }
      assertArmPayloadName(name, unionCase.name, unionCase.arm, switchKey);
      this.#casesByDiscriminant.set(unionCase.discriminant, unionCase);
    }
    if (defaultArm !== void 0) {
      assertArmPayloadName(name, DEFAULT_CASE_NAME, defaultArm, switchKey);
    }
  }
  _read(reader, path) {
    reader.enter(path);
    try {
      const discriminant = this.switchOn._read(
        reader,
        `${path}.${this.switchKey}`
      );
      const unionCase = this.#casesByDiscriminant.get(discriminant);
      const arm = unionCase?.arm ?? this.defaultArm;
      if (arm === void 0) {
        throw new XdrError(
          `${path}: no case for union discriminator ${String(discriminant)}`
        );
      }
      return readUnionArm(
        discriminant,
        arm,
        this.switchKey,
        reader,
        `${path}.${unionCase?.name ?? DEFAULT_CASE_NAME}`
      );
    } finally {
      reader.exit();
    }
  }
  _write(value, writer, path) {
    writer.enter(path);
    try {
      if (!isPlainObject(value) || !Object.prototype.hasOwnProperty.call(value, this.switchKey)) {
        throw new XdrError(
          `${path}: expected union object with ${this.switchKey} discriminator`
        );
      }
      const unionValue = value;
      const switchValue = unionValue[this.switchKey];
      const unionCase = this.#casesByDiscriminant.get(switchValue);
      const arm = unionCase?.arm ?? this.defaultArm;
      if (arm === void 0) {
        throw new XdrError(
          `${path}: no case for union discriminator ${String(switchValue)}`
        );
      }
      this.switchOn._write(switchValue, writer, `${path}.${this.switchKey}`);
      writeUnionArm(
        unionValue,
        arm,
        writer,
        `${path}.${unionCase?.name ?? DEFAULT_CASE_NAME}`
      );
    } finally {
      writer.exit();
    }
  }
}
function field(name, schema) {
  return { kind: "field", name, schema };
}
function case_(name, discriminant, arm) {
  return { name, discriminant, arm };
}
function union(name, options) {
  const switchKey = options.switchKey ?? "type";
  return new UnionType(
    name,
    options.switchOn,
    options.cases,
    options.defaultArm,
    switchKey
  );
}
function readUnionArm(discriminant, arm, switchKey, reader, path) {
  const result = {};
  result[switchKey] = discriminant;
  if (isFieldArm(arm)) {
    result[arm.name] = arm.schema._read(reader, `${path}.${arm.name}`);
    return result;
  }
  arm._read(reader, path);
  return result;
}
function writeUnionArm(value, arm, writer, path) {
  if (isFieldArm(arm)) {
    if (!Object.prototype.hasOwnProperty.call(value, arm.name)) {
      throw new XdrError(`${path}.${arm.name}: missing union arm payload`);
    }
    arm.schema._write(value[arm.name], writer, `${path}.${arm.name}`);
    return;
  }
  arm._write(void 0, writer, path);
}
function assertArmPayloadName(unionName, caseName, arm, switchKey) {
  if (isFieldArm(arm) && arm.name === switchKey) {
    throw new XdrError(
      `${unionName}.${caseName}: union arm payload field must not be named ${switchKey}`
    );
  }
  if (isFieldArm(arm) && arm.name === "__proto__") {
    throw new XdrError(
      `${unionName}.${caseName}: union arm payload field name '__proto__' is not allowed`
    );
  }
}
function isFieldArm(arm) {
  return "schema" in arm;
}

class VarOpaqueType extends BaseType {
  constructor(maxLength, name) {
    super(name);
    this.maxLength = maxLength;
    assertLength(maxLength, "varOpaque maxLength");
  }
  maxLength;
  kind = "varOpaque";
  _read(reader, path) {
    const length = reader.readUint32(path);
    if (length > this.maxLength) {
      throw new XdrError(
        `${path}: opaque length ${length} exceeds maximum ${this.maxLength}`
      );
    }
    const bytes = reader.readBytes(length, path);
    reader.skipPadding(length, path);
    return bytes;
  }
  _write(value, writer, path) {
    assertUint8Array(value, path);
    if (value.length > this.maxLength) {
      throw new XdrError(
        `${path}: opaque length ${value.length} exceeds maximum ${this.maxLength}`
      );
    }
    writer.writeUint32(value.length);
    writer.writeBytes(value);
    writer.writePadding(value.length);
  }
}
function varOpaque(maxLength, name) {
  return new VarOpaqueType(maxLength, name);
}

class VoidType extends BaseType {
  kind = "void";
  _read() {
    return void 0;
  }
  _write(value, _writer, path) {
    if (value !== void 0) {
      throw new XdrError(`${path}: expected void`);
    }
  }
}
function void_() {
  return new VoidType();
}

exports.BaseType = BaseType;
exports.Reader = Reader;
exports.UNBOUNDED_MAX_LENGTH = UNBOUNDED_MAX_LENGTH;
exports.Writer = Writer;
exports.XdrError = XdrError;
exports.array = array;
exports.bool = bool;
exports.case = case_;
exports.double = double;
exports.enumType = enumType;
exports.field = field;
exports.fixedArray = fixedArray;
exports.float = float;
exports.int32 = int32;
exports.int64 = int64;
exports.lazy = lazy;
exports.opaque = opaque;
exports.option = option;
exports.string = string_;
exports.struct = struct;
exports.uint32 = uint32;
exports.uint64 = uint64;
exports.union = union;
exports.unionCase = case_;
exports.varOpaque = varOpaque;
exports.void = void_;
exports.voidType = void_;
//# sourceMappingURL=js-xdr.cjs.map
